import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff, Shield, KeyRound, AlertCircle } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { authAPI } from '../../services/api';
import {
  SSO_POPUP_NAME,
  buildCenteredPopupFeatures,
  markSsoPopupFlow,
  clearSsoPopupFlow,
  subscribeToSsoResult,
} from '../../utils/ssoPopup';
import './Login.css';

// The loginUrl carries a state JWT that expires after 10 minutes, so refresh it
// while the page sits open — the popup must be opened synchronously on click.
const SSO_CONFIG_REFRESH_MS = 5 * 60 * 1000;
// Give the popup a moment to settle before trusting `popup.closed` (a
// cross-origin navigation can briefly report a closed handle).
const SSO_CLOSE_GRACE_MS = 1500;

const Login = () => {
  const { login, loginWithSso } = useAppContext();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  // NLNG SSO is optional — the button only renders when the backend reports
  // it as configured ({ enabled, loginUrl }).
  const [ssoConfig, setSsoConfig] = useState(null);
  const [ssoPending, setSsoPending] = useState(false);
  const [ssoError, setSsoError] = useState(null);

  const ssoPopupRef = useRef(null);
  const ssoPollRef = useRef(null);
  // Guards against handling the same sign-in twice (the popup reports its
  // result over both postMessage and BroadcastChannel).
  const ssoHandledRef = useRef(false);

  useEffect(() => {
    const savedEmail = localStorage.getItem('login-email');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const fetchSsoConfig = useCallback(async () => {
    try {
      const response = await authAPI.getSsoConfig();
      setSsoConfig(response.data?.data || null);
    } catch (error) {
      // SSO unavailable (not configured or backend unreachable) — hide the button
      setSsoConfig(null);
    }
  }, []);

  useEffect(() => {
    fetchSsoConfig();
    const timer = setInterval(fetchSsoConfig, SSO_CONFIG_REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchSsoConfig]);

  const stopSsoPopupWatch = useCallback(() => {
    if (ssoPollRef.current) {
      clearInterval(ssoPollRef.current);
      ssoPollRef.current = null;
    }
    clearSsoPopupFlow();
  }, []);

  useEffect(() => () => stopSsoPopupWatch(), [stopSsoPopupWatch]);

  // Result of the popup sign-in: the popup hands back the one-time CAS ticket
  // and this (the opener) exchanges it, so the session lands in this tab.
  const handleSsoResult = useCallback(async (payload) => {
    if (ssoHandledRef.current) return;
    ssoHandledRef.current = true;
    stopSsoPopupWatch();

    try {
      if (ssoPopupRef.current && !ssoPopupRef.current.closed) ssoPopupRef.current.close();
    } catch (error) {
      /* popup already gone */
    }
    ssoPopupRef.current = null;

    if (!payload.ticket) {
      setSsoPending(false);
      setSsoError(payload.error || 'No SSO ticket was received. Please try signing in again.');
      fetchSsoConfig();
      return;
    }

    const result = await loginWithSso(payload.ticket, payload.state || '');
    if (result.success) {
      const intendedRoute = sessionStorage.getItem('intendedRoute') || '/';
      sessionStorage.removeItem('intendedRoute');
      navigate(intendedRoute, { replace: true });
      return;
    }

    setSsoPending(false);
    setSsoError(result.error);
    // The ticket and state are spent — get a fresh login URL for the retry.
    fetchSsoConfig();
  }, [loginWithSso, navigate, stopSsoPopupWatch, fetchSsoConfig]);

  useEffect(() => subscribeToSsoResult(handleSsoResult), [handleSsoResult]);

  const handleSsoLogin = () => {
    const loginUrl = ssoConfig?.loginUrl;
    if (!loginUrl || ssoPending) return;

    setSsoError(null);
    ssoHandledRef.current = false;
    markSsoPopupFlow();

    let popup = null;
    try {
      popup = window.open(loginUrl, SSO_POPUP_NAME, buildCenteredPopupFeatures());
    } catch (error) {
      popup = null;
    }

    // Popup blocked — fall back to the full-page redirect so nobody is stuck.
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      clearSsoPopupFlow();
      setSsoPending(true);
      window.location.assign(loginUrl);
      return;
    }

    ssoPopupRef.current = popup;
    setSsoPending(true);
    try {
      popup.focus();
    } catch (error) {
      /* focus is best-effort */
    }

    // Detect the user closing the popup without completing sign-in.
    const openedAt = Date.now();
    ssoPollRef.current = setInterval(() => {
      let closed = false;
      try {
        closed = popup.closed;
      } catch (error) {
        closed = false;
      }
      if (!closed || Date.now() - openedAt < SSO_CLOSE_GRACE_MS) return;

      stopSsoPopupWatch();
      ssoPopupRef.current = null;
      if (ssoHandledRef.current) return;
      setSsoPending(false);
      setSsoError('The SSO sign-in window was closed before sign-in completed.');
      fetchSsoConfig();
    }, 500);
  };

  const cancelSsoLogin = () => {
    ssoHandledRef.current = true;
    stopSsoPopupWatch();
    try {
      if (ssoPopupRef.current && !ssoPopupRef.current.closed) ssoPopupRef.current.close();
    } catch (error) {
      /* popup already gone */
    }
    ssoPopupRef.current = null;
    setSsoPending(false);
    setSsoError(null);
    fetchSsoConfig();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      return;
    }

    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      if (rememberMe) {
        localStorage.setItem('login-email', email);
      } else {
        localStorage.removeItem('login-email');
      }
      navigate('/');
    }

    setLoading(false);
  };

  return (
    <div className="login-simple">
      <div className="login-simple__container">
        {/* Logo and Brand */}
        <div className="login-simple__brand">
          <div className="login-simple__logo">
            <Shield size={40} strokeWidth={2} />
            <img src="/assets/nlng-logo.png" alt="NLNG Logo" className="sidebar__logo-image" />
          </div>
          <h1 className="login-simple__brand-name">NLNG VR</h1>
          <p className="login-simple__brand-subtitle">VR Training Platform for NLNG Staff</p>
        </div>

        {/* Login Form */}
        <div className="login-simple__form-wrapper">

          <form onSubmit={handleSubmit} className="login-simple__form">
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                className="form-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoFocus
              />
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="login-simple__password-input">
                <input
                  className="form-input"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="login-simple__password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="login-simple__options">
              <label className="login-simple__checkbox">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
            </div>

            <button
              type="submit"
              className="btn btn-primary login-simple__submit"
              disabled={loading || !email.trim() || !password.trim()}
            >
              {loading ? (
                <span className="login-simple__spinner" />
              ) : (
                <>
                  <LogIn size={18} />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* NLNG SSO — shown only when the backend has SSO configured */}
          {ssoConfig?.enabled && (
            <div className="login-simple__sso">
              <div className="login-simple__divider">
                <span>or</span>
              </div>
              <button
                type="button"
                className="btn login-simple__sso-button"
                onClick={handleSsoLogin}
                disabled={ssoPending}
              >
                {ssoPending ? (
                  <span className="login-simple__spinner login-simple__spinner--green" />
                ) : (
                  <>
                    <KeyRound size={18} />
                    Sign in with NLNG SSO
                  </>
                )}
              </button>

              {ssoPending && (
                <p className="login-simple__sso-status">
                  Continue in the NLNG sign-in window.{' '}
                  <button type="button" className="login-simple__sso-cancel" onClick={cancelSsoLogin}>
                    Cancel
                  </button>
                </p>
              )}

              {ssoError && !ssoPending && (
                <p className="login-simple__sso-error">
                  <AlertCircle size={16} />
                  <span>{ssoError}</span>
                </p>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="login-simple__footer">
            <p className="login-simple__footer-text">
              By signing in, you agree to our{' '}
              <button type="button" className="login-simple__footer-link" onClick={() => alert('Terms of Service page coming soon!')}>
                Terms of Service
              </button>{' '}
              and{' '}
              <button type="button" className="login-simple__footer-link" onClick={() => alert('Privacy Policy page coming soon!')}>
                Privacy Policy
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;